import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { startOfMonth, endOfMonth } from 'date-fns';

const InvoiceAnalytics = ({ invoices }) => {
  const analytics = useMemo(() => {
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);

    // This month's invoices
    const thisMonthInvoices = invoices?.filter(inv => {
      const invDate = new Date(inv?.billing_date);
      return invDate >= monthStart && invDate <= monthEnd;
    });

    const thisMonthRevenue = thisMonthInvoices
      ?.filter(inv => inv?.payment_status === 'completed')
      ?.reduce((sum, inv) => sum + parseFloat(inv?.total_amount || 0), 0);

    const thisMonthPending = thisMonthInvoices
      ?.filter(inv => inv?.payment_status === 'pending')
      ?.reduce((sum, inv) => sum + parseFloat(inv?.total_amount || 0), 0);

    // Payment success rate
    const totalProcessed = invoices?.filter(
      inv => inv?.payment_status === 'completed' || inv?.payment_status === 'failed'
    )?.length || 1;
    const successfulPayments = invoices?.filter(
      inv => inv?.payment_status === 'completed'
    )?.length || 0;
    const successRate = (successfulPayments / totalProcessed) * 100;

    // Average invoice value
    const avgInvoiceValue = invoices?.length > 0
      ? invoices?.reduce((sum, inv) => sum + parseFloat(inv?.total_amount || 0), 0) / invoices?.length
      : 0;

    return {
      thisMonthRevenue,
      thisMonthPending,
      successRate,
      avgInvoiceValue,
      thisMonthCount: thisMonthInvoices?.length
    };
  }, [invoices]);

  return (
    <div className="mb-6 bg-card border border-border rounded-lg shadow-md p-6">
      <h2 className="text-xl font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name="BarChart3" size={24} className="text-primary" />
        Invoice Analytics
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon name="TrendingUp" size={20} className="text-green-600" />
            <p className="text-sm text-muted-foreground">This Month Revenue</p>
          </div>
          <p className="text-3xl font-bold text-foreground">
            ${analytics?.thisMonthRevenue?.toFixed(2)}
          </p>
          <p className="text-xs text-muted-foreground">
            From {analytics?.thisMonthCount} invoices
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={20} className="text-yellow-600" />
            <p className="text-sm text-muted-foreground">Pending This Month</p>
          </div>
          <p className="text-3xl font-bold text-foreground">
            ${analytics?.thisMonthPending?.toFixed(2)}
          </p>
          <p className="text-xs text-muted-foreground">
            Expected revenue
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon name="Target" size={20} className="text-blue-600" />
            <p className="text-sm text-muted-foreground">Payment Success Rate</p>
          </div>
          <p className="text-3xl font-bold text-foreground">
            {analytics?.successRate?.toFixed(1)}%
          </p>
          <p className="text-xs text-muted-foreground">
            All time average
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon name="DollarSign" size={20} className="text-purple-600" />
            <p className="text-sm text-muted-foreground">Avg Invoice Value</p>
          </div>
          <p className="text-3xl font-bold text-foreground">
            ${analytics?.avgInvoiceValue?.toFixed(2)}
          </p>
          <p className="text-xs text-muted-foreground">
            Per invoice
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceAnalytics;